import imageLoader from '../../lib/imageLoader';

export interface LightboxOptions {
	type?: string;
	target?: string;
	triggerEl?: HTMLCollection | NodeList | HTMLElement[];
	onOpen?: Function;
	onClose?: Function;
	onFirstOpen?: Function;
}

// NOTE: Likely want to refactor Lightbox to handle single lightbox triggers, rather than multiple. -SB 2018-01-17
// NOTE: Really, the whole thing should be tossed out and MagnificPopup used with new TS definitions, but it works. -SB 2018-02-19
export default class Lightbox {
	private type: string = 'image';
	public el: HTMLElement; /** A reference to the lightbox element */
	private target: string; /** The DOM element to load into the lightbox */
	private triggerEl: HTMLElement[] = []; /** The elements that trigger the lightbox */
	private onOpen: Function;
	private onClose: Function;
	private onFirstOpen: Function;
	private isFirstOpen: boolean = true;
	private activatedIndex: number;

	private lightboxTemplateEl: HTMLElement; /** The template used for the lightbox element */

	constructor(lightboxConfig: LightboxOptions) {
		// NOTE: Doesn't work right on old Android (limited support), so just turn it off.
		if (document.documentElement.classList.contains('no-flexbox'))
			return;

		this.type = lightboxConfig.type || this.type;
		this.target = lightboxConfig.target;
		for (let i = 0; i < lightboxConfig.triggerEl.length; i++) {
			this.triggerEl.push(lightboxConfig.triggerEl[i] as HTMLElement);
		}
		this.onOpen = lightboxConfig.onOpen;
		this.onClose = lightboxConfig.onClose;
		this.onFirstOpen = lightboxConfig.onFirstOpen;

		this.lightboxTemplateEl = this.buildLightbox();

		if (this.triggerEl) {
			for (let index: number = 0; index < this.triggerEl.length; index++) {
				const element: Element = this.triggerEl[index];
				element.addEventListener('click', this.openCB);
			}
		}
	}

	private buildLightbox(): HTMLElement {
		const lightboxEl: HTMLElement = document.createElement('div');
		lightboxEl.classList.add('lightbox');

		if (this.type) {
			lightboxEl.classList.add(`lightbox--${this.type}`);
		}

		let lightboxContents: string;
		lightboxEl.innerHTML =
			`<div class="lightbox__overlay"></div>
			<div class="lightbox__inner">
				<i class="lightbox__close"></i>
				<div class="lightbox__frame">
					${lightboxContents ? lightboxContents : ''}
				</div>
			</div>`;

		const lightboxCloseEl: Element = lightboxEl.getElementsByClassName('lightbox__close')[0];
		lightboxCloseEl.addEventListener('click', this.closeCB);

		const lightboxOverlayEl: Element = lightboxEl.getElementsByClassName('lightbox__overlay')[0];
		lightboxOverlayEl.addEventListener('click', this.closeCB);

		document.addEventListener('keydown', (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				this.close(event);
			}
		});

		if (this.target && this.type === 'dom') {
			const targetEl: Node = document.getElementsByClassName(this.target)[0].cloneNode(true);
			const lightboxContentEl: Element = lightboxEl.getElementsByClassName('lightbox__frame')[0];
			lightboxContentEl.appendChild(targetEl);
		}

		return lightboxEl;
	}

	public open(event?: Event): void {
		document.documentElement.classList.add('lightbox--open');
		this.el = document.body.insertBefore(this.lightboxTemplateEl, document.body.children[0]);

		const frameEl = this.el.getElementsByClassName('lightbox__frame')[0] as HTMLElement;
		if (this.type === 'image') {
			const imageAnchor = this.triggerEl[this.activatedIndex] as HTMLAnchorElement;
			const imageInfo = imageAnchor.getElementsByTagName('img')[0];

			imageLoader([imageAnchor.href])
				.then(response => {
					const imageTemplate = document.createElement('img');
					imageTemplate.src = response[0];
					imageTemplate.alt = imageInfo.alt;
					imageTemplate.title = imageInfo.title;

					frameEl.appendChild(imageTemplate);
					frameEl.style.height = `${frameEl.getBoundingClientRect().height}px`;
				})
				.catch(err => {
					console.log(err);
				});
		} else if (this.type === 'video') {
			let videoSrc = (<HTMLAnchorElement>this.triggerEl[this.activatedIndex]).href || this.triggerEl[this.activatedIndex].getAttribute('data-video-src');
			if (videoSrc) {
				// Parse embed for Vimeo
				if (videoSrc.indexOf('vimeo.com') > -1) {
					videoSrc = videoSrc.replace(/https?:\/\/(?:www.)?vimeo.com\/(.*?)$/, 'https://player.vimeo.com/video/$1');
				}
				const videoIframe = document.createElement('iframe');
				videoIframe.src = videoSrc;
				videoIframe.width = '1280px';
				videoIframe.height = '768px';
				videoIframe.frameBorder = '0';
				videoIframe.allowFullscreen = true;
				videoIframe.setAttribute('allow', 'encrypted-media');
				videoIframe.onload = () => { frameEl.classList.add('lightbox--videoLoaded'); };

				const frameEl = this.el.getElementsByClassName('lightbox__frame')[0];
				frameEl.appendChild(videoIframe);
			}
		}

		if (this.isFirstOpen && this.onFirstOpen) {
			this.onFirstOpen(event);
			this.isFirstOpen = false;
		}
		if (this.onOpen) {
			this.onOpen(event);
		}
	}

	public openCB = (event?: Event): void => {
		event.preventDefault();
		this.activatedIndex = this.triggerEl.indexOf(event.currentTarget as HTMLElement);
		this.open(event);
	}

	public close(event: Event): void {
		this.activatedIndex = undefined;
		document.documentElement.classList.remove('lightbox--open');

		// NOTE: This is to handle the gallery component's template
		const frameEl = this.el.getElementsByClassName('lightbox__frame')[0] as HTMLElement;
		if (this.type === 'image' || this.type === 'video') {
			frameEl.style.height = '';
			while (frameEl.firstChild) {
				frameEl.removeChild(frameEl.firstChild);
			}
		}

		if (this.type === 'carousel') {
			const slideEl = this.el.getElementsByClassName('lightbox__slide');
			for (let i = slideEl.length - 1; i >= 0; i--) {
				frameEl.removeChild(slideEl[i]);
			}

			const controlsEl = this.el.getElementsByClassName('lightbox__controls')[0];
			if (controlsEl) {
				controlsEl.parentElement.removeChild(controlsEl);
			}
		}

		if (this.type === 'video') {
			frameEl.classList.remove('lightbox--videoLoaded');
		}

		document.body.removeChild(this.el);
		if (this.onClose) {
			this.onClose(event);
		}
	}

	public closeCB = (event: Event): void => {
		this.close(event);
	}
}
